import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Compass, Loader2, Play, RefreshCw, RotateCcw, ShieldAlert, ShieldCheck, Square, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    fetchSgBlueprint, fetchSgBlueprintPaper, fetchSgBlueprintRun, fetchSgBlueprintRuns,
    resetSgBlueprintPaper, runSgBlueprintReplay, startSgBlueprintPaper,
    stopSgBlueprintPaper, tuneSgBlueprintPaper,
} from "@/lib/smartgrid_api";

const C = {
    bg: "#090B0F", panel: "#0F1217", border: "#1B2A3A", text: "#E6EDF6",
    sub: "#A7B4C6", dim: "#6F7D92", bull: "#34D399", bear: "#F87171",
    warn: "#FBBF24", accent: "#60A5FA",
};

const fmt = (v, d = 2) => (v === null || v === undefined || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(d));
const fmtTs = (n) => (n ? new Date(n).toLocaleString() : "—");

// Coerce a form field to a number the backend accepts: empty/garbage falls
// back to the default, out-of-range clamps — a mistyped field must never
// make the replay silently refuse to run.
const num = (v, def, lo, hi) => {
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return def;
    return Math.min(hi, Math.max(lo, n));
};

// Readable message out of any API error (FastAPI 422s carry an ARRAY of
// {loc, msg} objects — rendered raw they show as nothing useful).
const apiErr = (e) => {
    const d = e?.response?.data?.detail;
    if (Array.isArray(d)) {
        return d.map((x) => `${(x?.loc || []).slice(1).join(".")}: ${x?.msg || "invalid"}`).join(" · ");
    }
    if (typeof d === "string") return d;
    return String(e?.message || e);
};

const Tile = ({ label, value, tone = C.text, hint }) => (
    <div className="border px-3 py-2" style={{ borderColor: C.border, background: C.panel }} title={hint || ""}>
        <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: C.dim }}>{label}</div>
        <div className="font-mono text-sm mt-0.5" style={{ color: tone }}>{value}</div>
    </div>
);

const Flag = ({ on, label }) => (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wide border"
        style={{
            color: on ? C.bear : C.bull, borderColor: on ? "#F8717140" : "#34D39940",
            background: on ? "#F8717112" : "#34D39912",
        }}>
        {on ? <ShieldAlert size={11} /> : <ShieldCheck size={11} />} {label}{on ? "" : " ok"}
    </span>
);

const EquityCurve = ({ curve, budget, testId = "bp-equity-curve" }) => {
    if (!curve || curve.length < 2) return <div className="text-[11px]" style={{ color: C.dim }}>no curve</div>;
    const w = 640, h = 120, pad = 4;
    const ys = curve.map((p) => p[1]);
    const lo = Math.min(...ys), hi = Math.max(...ys);
    const span = Math.max(1e-9, hi - lo);
    const pts = curve.map((p, i) => {
        const x = pad + (i / (curve.length - 1)) * (w - 2 * pad);
        const y = h - pad - ((p[1] - lo) / span) * (h - 2 * pad);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    const baseY = budget >= lo && budget <= hi
        ? h - pad - ((budget - lo) / span) * (h - 2 * pad) : null;
    return (
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ maxHeight: 130 }} data-testid={testId}>
            {baseY !== null && (
                <line x1={0} x2={w} y1={baseY} y2={baseY} stroke={C.dim} strokeDasharray="4 4" strokeWidth="1" />
            )}
            <polyline points={pts} fill="none"
                stroke={ys[ys.length - 1] >= ys[0] ? C.bull : C.bear} strokeWidth="1.5" />
        </svg>
    );
};

const ResultRow = ({ r, best, selected, onClick }) => {
    const mk30 = r.markout_bps?.["30s"] || {};
    const mk300 = r.markout_bps?.["300s"] || {};
    const tone = (v) => (v > 0 ? C.bull : v < 0 ? C.bear : C.sub);
    return (
        <tr onClick={onClick} className="cursor-pointer hover:bg-[#121C28]"
            style={{ background: selected ? "#121C28" : "transparent" }}
            data-testid={`bp-result-k-${r.params.k_sigma}`}>
            <td className="px-2 py-1.5 font-mono text-xs" style={{ color: best ? C.accent : C.text }}>
                {r.params.k_sigma}{best ? " ★" : ""}
            </td>
            <td className="px-2 py-1.5 font-mono text-xs text-right" style={{ color: tone(r.net_pnl) }}>${fmt(r.net_pnl)}</td>
            <td className="px-2 py-1.5 font-mono text-xs text-right" style={{ color: C.sub }}>{r.cycles}</td>
            <td className="px-2 py-1.5 font-mono text-xs text-right" style={{ color: C.sub }}>{fmt(r.win_rate, 1)}%</td>
            <td className="px-2 py-1.5 font-mono text-xs text-right" style={{ color: r.max_dd_pct_of_budget > 100 * (r.params.dd_stop_pct || 0.08) * 0.8 ? C.warn : C.sub }}>
                {fmt(r.max_dd_pct_of_budget, 1)}%
            </td>
            <td className="px-2 py-1.5 font-mono text-xs text-right" style={{ color: C.sub }}>{fmt(r.sharpe_hourly, 2)}</td>
            <td className="px-2 py-1.5 font-mono text-xs text-right" style={{ color: tone(mk30.mean) }}>
                {fmt(mk30.mean, 1)} <span style={{ color: C.dim }}>({mk30.n || 0})</span>
            </td>
            <td className="px-2 py-1.5 font-mono text-xs text-right" style={{ color: tone(mk300.mean) }}>{fmt(mk300.mean, 1)}</td>
            <td className="px-2 py-1.5 font-mono text-xs text-right" style={{ color: C.dim }}>{fmt(r.time_paused_pct, 0)}%</td>
            <td className="px-2 py-1.5 text-xs text-right" style={{ color: r.halted ? C.bear : C.dim }}>
                {r.halted ? "HALTED" : "—"}
            </td>
        </tr>
    );
};

export default function SmartGridBlueprintPage({ onBack }) {
    const [live, setLive] = useState(null);
    const [form, setForm] = useState({
        hours: 48, k_list: "0.5, 0.75, 1.0, 1.5, 2.0", depth: 6,
        rung_usd: 150, fee_bps: 10, dd_stop_pct: 8, use_recorded_events: true,
        note: "",
    });
    const [running, setRunning] = useState(false);
    const [replay, setReplay] = useState(null);
    const [sel, setSel] = useState(0);
    const [runs, setRuns] = useState([]);
    const [paper, setPaper] = useState(null);
    const [paperForm, setPaperForm] = useState({
        k_sigma: 1.0, depth: 6, rung_usd: 150, fee_bps: 10, dd_stop_pct: 8,
        auto_tune: true, auto_tune_hours: 6,
    });
    const [paperBusy, setPaperBusy] = useState(false);

    const refresh = useCallback(() => {
        fetchSgBlueprint().then(setLive).catch(() => setLive(null));
        fetchSgBlueprintRuns().then((d) => setRuns(d.runs || [])).catch(() => {});
        fetchSgBlueprintPaper().then(setPaper).catch(() => setPaper(null));
    }, []);
    useEffect(() => {
        refresh();
        const id = setInterval(refresh, 5000);
        return () => clearInterval(id);
    }, [refresh]);

    const run = async () => {
        // parse + clamp every field client-side (the backend clamps too):
        // an empty or mistyped field falls back to its default instead of
        // silently dead-ending the run with a validation error
        const k_list = form.k_list.split(",")
            .map((s) => parseFloat(s.trim()))
            .filter((k) => Number.isFinite(k) && k > 0)
            .map((k) => Math.min(4, Math.max(0.25, k)));
        if (!k_list.length) { toast.error("k list is empty — enter comma-separated multipliers, e.g. 0.5, 1, 2"); return; }
        setRunning(true);
        try {
            const out = await runSgBlueprintReplay({
                hours: num(form.hours, 48, 1, 336), k_list,
                depth: Math.round(num(form.depth, 6, 1, 20)),
                rung_usd: num(form.rung_usd, 150, 10, 5000),
                fee_bps: num(form.fee_bps, 10, 0, 50),
                dd_stop_pct: num(form.dd_stop_pct, 8, 2, 50),
                use_recorded_events: !!form.use_recorded_events,
                note: form.note || "",
            });
            setReplay(out);
            const bi = (out.results || []).findIndex((r) => r.params.k_sigma === out.best_k);
            setSel(bi >= 0 ? bi : 0);
            fetchSgBlueprintRuns().then((d) => setRuns(d.runs || [])).catch(() => {});
            toast.success(`replayed ${out.rows.toLocaleString()} marks / ${out.events} events`
                + (out.run_id ? ` · saved as ${out.run_id}` : ""));
        } catch (e) {
            toast.error(apiErr(e));
        } finally {
            setRunning(false);
        }
    };

    const paperAction = async (fn, okMsg) => {
        setPaperBusy(true);
        try {
            const out = await fn();
            setPaper(out.status || out);
            if (okMsg) toast.success(okMsg);
            return out;
        } catch (e) {
            toast.error(apiErr(e));
        } finally {
            setPaperBusy(false);
        }
    };
    const startPaper = () => paperAction(() => startSgBlueprintPaper({
        k_sigma: num(paperForm.k_sigma, 1, 0.25, 4),
        depth: Math.round(num(paperForm.depth, 6, 1, 20)),
        rung_usd: num(paperForm.rung_usd, 150, 10, 5000),
        fee_bps: num(paperForm.fee_bps, 10, 0, 50),
        dd_stop_pct: num(paperForm.dd_stop_pct, 8, 2, 50),
        auto_tune: !!paperForm.auto_tune,
        auto_tune_hours: num(paperForm.auto_tune_hours, 6, 1, 72),
    }), "paper grid started (fresh book)");
    const stopPaper = () => paperAction(stopSgBlueprintPaper, "paper grid stopped");
    const resetPaper = () => paperAction(resetSgBlueprintPaper, "paper book reset");
    const tunePaper = async () => {
        const out = await paperAction(tuneSgBlueprintPaper);
        if (out?.tune) {
            toast[out.tune.applied ? "success" : "info"](
                `auto-tune: ${out.tune.reason || "done"}`
                + (out.tune.best_k !== undefined ? ` (best k=${out.tune.best_k})` : ""));
        }
    };

    const st = live?.state || {};
    const pol = live?.policy || {};
    const dd = live?.dd_stop || {};
    const selRes = useMemo(() => (replay?.results || [])[sel], [replay, sel]);
    const field = (k, props = {}) => (
        <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            className="h-8 w-24 bg-[#0F1217] border-[#1B2A3A] text-xs font-mono" {...props} />
    );
    const pfield = (k, props = {}) => (
        <Input value={paperForm[k]} onChange={(e) => setPaperForm({ ...paperForm, [k]: e.target.value })}
            className="h-8 w-20 bg-[#0F1217] border-[#1B2A3A] text-xs font-mono"
            data-testid={`bp-paper-${k}`} {...props} />
    );

    return (
        <div className="min-h-screen" style={{ background: C.bg, color: C.text }} data-testid="sg-blueprint-page">
            <div className="max-w-[1100px] mx-auto px-4 py-4">
                <div className="flex items-center gap-3 mb-4">
                    <Button variant="ghost" size="sm" onClick={onBack} style={{ color: C.sub }} data-testid="bp-back">
                        <ArrowLeft size={15} /> SmartGrid
                    </Button>
                    <Compass size={16} color={C.accent} />
                    <span className="text-sm tracking-wide">Blueprint — minimal grid stack</span>
                    <span className="text-[10px] uppercase px-2 py-0.5 rounded border"
                        style={{ color: C.warn, borderColor: "#FBBF2440", background: "#FBBF2410" }}>
                        shadow only — places no orders
                    </span>
                    <Button variant="ghost" size="sm" onClick={refresh} className="ml-auto" style={{ color: C.sub }}>
                        <RefreshCw size={14} /> Refresh
                    </Button>
                </div>

                {/* L1 state */}
                <div className="text-[11px] uppercase tracking-[0.16em] mb-2" style={{ color: C.sub }}>
                    L1 · State (eight numbers — one estimator per quantity)
                </div>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-4" data-testid="bp-state">
                    <Tile label="Mid" value={st.mid ? `$${Number(st.mid).toLocaleString()}` : "—"} />
                    <Tile label="Spread" value={`${fmt(st.spread_bps, 2)} bps`} />
                    <Tile label="σ 1m" value={`${fmt(st.sigma_1m_pct, 3)}%`} hint="one volatility yardstick (ATR-derived)" />
                    <Tile label="Basis z" value={fmt(st.basis_z, 2)} tone={Math.abs(st.basis_z || 0) > 1.5 ? C.warn : C.text} />
                    <Tile label="Leader 5s" value={`${fmt(st.leader_mom_bps, 1)} bps`} />
                    <Tile label="Flow" value={fmt(st.flow, 3)} tone={(st.flow || 0) >= 0 ? C.bull : C.bear} />
                    <Tile label="Regime" value={st.regime3 || "—"}
                        tone={st.regime3 === "trend_up" ? C.bull : st.regime3 === "trend_down" ? C.bear : C.sub} />
                    <div className="border px-3 py-2 flex flex-col gap-1 justify-center"
                        style={{ borderColor: C.border, background: C.panel }}>
                        <Flag on={!!st.crash} label="crash" />
                        <Flag on={!!st.guard} label="guard" />
                    </div>
                </div>

                {/* L2/L3 policy + hard stop */}
                <div className="text-[11px] uppercase tracking-[0.16em] mb-2" style={{ color: C.sub }}>
                    L2/L3 · Shadow policy + hard stop
                </div>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-4" data-testid="bp-policy">
                    <Tile label="Spacing" value={`${fmt(pol.spacing_bps, 1)} bps`} hint="k × hourly σ, clamped" />
                    <Tile label="Spacing $" value={pol.spacing_usd ? `$${fmt(pol.spacing_usd, 0)}` : "—"} />
                    <Tile label="Rungs" value={pol.rungs ?? "—"} />
                    <Tile label="Budget" value={pol.budget_usdt ? `$${pol.budget_usdt}` : "—"} />
                    <Tile label="Buys" value={pol.buys_paused ? `PAUSED (${(pol.pause_reasons || []).join("+") || "?"})` : "active"}
                        tone={pol.buys_paused ? C.bear : C.bull} />
                    <Tile label="Trail TP" value={pol.trail_tp ? "on (trend up)" : "off"} />
                    <Tile label="DD / stop" value={dd.available ? `${fmt(dd.dd_pct_of_budget, 1)}% / ${fmt(dd.stop_at_pct, 0)}%` : "—"}
                        tone={dd.would_halt ? C.bear : C.text}
                        hint="drawdown vs high-water mark, as % of budget" />
                    <Tile label="Would halt" value={dd.available ? (dd.would_halt ? "YES" : "no") : "—"}
                        tone={dd.would_halt ? C.bear : C.bull} />
                </div>

                {/* Replay harness */}
                <div className="text-[11px] uppercase tracking-[0.16em] mb-2" style={{ color: C.sub }}>
                    L0/L5 · Replay harness (recorded marks + recorded guard events)
                </div>
                <div className="border p-3 mb-3" style={{ borderColor: C.border, background: C.panel }}>
                    <div className="flex flex-wrap items-end gap-3" data-testid="bp-replay-form">
                        <div><div className="text-[10px] mb-1" style={{ color: C.dim }}>Hours</div>{field("hours")}</div>
                        <div><div className="text-[10px] mb-1" style={{ color: C.dim }}>k list (× hourly σ)</div>
                            <Input value={form.k_list} onChange={(e) => setForm({ ...form, k_list: e.target.value })}
                                className="h-8 w-52 bg-[#0F1217] border-[#1B2A3A] text-xs font-mono" /></div>
                        <div><div className="text-[10px] mb-1" style={{ color: C.dim }}>Depth</div>{field("depth")}</div>
                        <div><div className="text-[10px] mb-1" style={{ color: C.dim }}>Rung $</div>{field("rung_usd")}</div>
                        <div><div className="text-[10px] mb-1" style={{ color: C.dim }}>Fee bps/side</div>{field("fee_bps")}</div>
                        <div><div className="text-[10px] mb-1" style={{ color: C.dim }}>DD stop %</div>{field("dd_stop_pct")}</div>
                        <div><div className="text-[10px] mb-1" style={{ color: C.dim }}>Note (saved with run)</div>
                            <Input value={form.note} placeholder="e.g. wider fee test"
                                onChange={(e) => setForm({ ...form, note: e.target.value })}
                                className="h-8 w-44 bg-[#0F1217] border-[#1B2A3A] text-xs" /></div>
                        <label className="flex items-center gap-1.5 text-[11px] pb-2" style={{ color: C.sub }}>
                            <input type="checkbox" checked={form.use_recorded_events}
                                onChange={(e) => setForm({ ...form, use_recorded_events: e.target.checked })} />
                            replay recorded guard/liq events
                        </label>
                        <Button size="sm" onClick={run} disabled={running}
                            className="bg-[#60A5FA] text-black hover:bg-[#7BB6FB] h-8" data-testid="bp-run-replay">
                            {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Run replay
                        </Button>
                    </div>
                    {replay && (
                        <div className="mt-2 text-[11px]" style={{ color: C.dim }}>
                            {replay.rows.toLocaleString()} mark rows · {replay.events} events · {fmtTs(replay.from_ts)} → {fmtTs(replay.to_ts)}
                        </div>
                    )}
                </div>

                {replay?.results?.length > 0 && (
                    <div className="border mb-3 overflow-x-auto" style={{ borderColor: C.border, background: C.panel }}>
                        <table className="w-full" data-testid="bp-results-table">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>
                                    <th className="px-2 py-2 text-left">k</th>
                                    <th className="px-2 py-2 text-right">Net P&L</th>
                                    <th className="px-2 py-2 text-right">Cycles</th>
                                    <th className="px-2 py-2 text-right">Win</th>
                                    <th className="px-2 py-2 text-right">Max DD</th>
                                    <th className="px-2 py-2 text-right">Sharpe/h</th>
                                    <th className="px-2 py-2 text-right">Markout 30s (n)</th>
                                    <th className="px-2 py-2 text-right">5m</th>
                                    <th className="px-2 py-2 text-right">Paused</th>
                                    <th className="px-2 py-2 text-right">Stop</th>
                                </tr>
                            </thead>
                            <tbody>
                                {replay.results.map((r, i) => (
                                    <ResultRow key={r.params.k_sigma} r={r} selected={i === sel}
                                        best={r.params.k_sigma === replay.best_k} onClick={() => setSel(i)} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {selRes && (
                    <div className="border p-3 mb-4" style={{ borderColor: C.border, background: C.panel }}>
                        <div className="text-[10px] uppercase tracking-[0.12em] mb-1" style={{ color: C.dim }}>
                            Equity curve — k={selRes.params.k_sigma} (dashed = starting budget ${selRes.params.depth * selRes.params.rung_usd})
                        </div>
                        <EquityCurve curve={selRes.equity_curve} budget={selRes.params.depth * selRes.params.rung_usd} />
                        <div className="text-[10px] mt-1" style={{ color: C.dim }}>
                            final ${fmt(selRes.final_equity)} · fees ${fmt(selRes.fees)} · unrealized ${fmt(selRes.unrealized)}
                            {selRes.halted ? ` · HALTED at ${fmtTs(selRes.halted_at_ms)} (hard stop)` : ""}
                        </div>
                    </div>
                )}

                {/* Paper trading grid */}
                <div className="text-[11px] uppercase tracking-[0.16em] mb-2" style={{ color: C.sub }}>
                    L6 · Paper trading grid (simulated fills on the live mark — places no orders)
                </div>
                <div className="border p-3 mb-4" style={{ borderColor: C.border, background: C.panel }} data-testid="bp-paper">
                    <div className="flex flex-wrap items-end gap-3" data-testid="bp-paper-form">
                        <div><div className="text-[10px] mb-1" style={{ color: C.dim }}>k (× hourly σ)</div>{pfield("k_sigma")}</div>
                        <div><div className="text-[10px] mb-1" style={{ color: C.dim }}>Depth</div>{pfield("depth")}</div>
                        <div><div className="text-[10px] mb-1" style={{ color: C.dim }}>Rung $</div>{pfield("rung_usd")}</div>
                        <div><div className="text-[10px] mb-1" style={{ color: C.dim }}>Fee bps/side</div>{pfield("fee_bps")}</div>
                        <div><div className="text-[10px] mb-1" style={{ color: C.dim }}>DD stop %</div>{pfield("dd_stop_pct")}</div>
                        <label className="flex items-center gap-1.5 text-[11px] pb-2" style={{ color: C.sub }}
                            title="every N hours: replay the last 48h of recorded ticks, sweep k, and apply the most profitable k to this paper grid">
                            <input type="checkbox" checked={paperForm.auto_tune}
                                onChange={(e) => setPaperForm({ ...paperForm, auto_tune: e.target.checked })}
                                data-testid="bp-paper-autotune" />
                            auto-tune k every
                        </label>
                        <div>{pfield("auto_tune_hours", { className: "h-8 w-14 bg-[#0F1217] border-[#1B2A3A] text-xs font-mono" })}</div>
                        <span className="text-[11px] pb-2 -ml-2" style={{ color: C.sub }}>h</span>
                        <Button size="sm" onClick={startPaper} disabled={paperBusy}
                            className="bg-[#34D399] text-black hover:bg-[#4BE0AC] h-8" data-testid="bp-paper-start">
                            <Play size={14} /> {paper?.active ? "Restart (fresh book)" : "Start paper grid"}
                        </Button>
                        {paper?.active && (
                            <>
                                <Button size="sm" variant="outline" onClick={stopPaper} disabled={paperBusy}
                                    className="h-8 border-[#1B2A3A] text-xs" style={{ color: C.bear }} data-testid="bp-paper-stop">
                                    <Square size={13} /> Stop
                                </Button>
                                <Button size="sm" variant="outline" onClick={tunePaper} disabled={paperBusy}
                                    className="h-8 border-[#1B2A3A] text-xs" style={{ color: C.accent }} data-testid="bp-paper-tune"
                                    title="run the auto-tune replay pass now">
                                    <Wand2 size={13} /> Tune now
                                </Button>
                            </>
                        )}
                        {paper?.started_ms > 0 && (
                            <Button size="sm" variant="outline" onClick={resetPaper} disabled={paperBusy}
                                className="h-8 border-[#1B2A3A] text-xs" style={{ color: C.warn }} data-testid="bp-paper-reset"
                                title="fresh book: budget restored, halt cleared">
                                <RotateCcw size={13} /> Reset book
                            </Button>
                        )}
                    </div>

                    {paper?.started_ms > 0 && (
                        <>
                            <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mt-3" data-testid="bp-paper-tiles">
                                <Tile label="State"
                                    value={paper.halted ? "HALTED" : paper.active ? (paper.paused ? `PAUSED (${(paper.pause_reasons || []).join("+")})` : "running") : "stopped"}
                                    tone={paper.halted ? C.bear : paper.active ? (paper.paused ? C.warn : C.bull) : C.dim}
                                    hint={paper.halted ? `hard stop fired ${fmtTs(paper.halted_at_ms)} — Reset book to clear` : ""} />
                                <Tile label="Equity" value={`$${fmt(paper.equity)}`}
                                    hint={`budget $${fmt(paper.budget_usdt, 0)} · cash $${fmt(paper.cash)}`} />
                                <Tile label="Net P&L" value={`$${fmt(paper.net_pnl)}`}
                                    tone={paper.net_pnl > 0 ? C.bull : paper.net_pnl < 0 ? C.bear : C.text}
                                    hint={`unrealized $${fmt(paper.unrealized)} · fees $${fmt(paper.fees)}`} />
                                <Tile label="Cycles / win" value={`${paper.cycles} / ${fmt(paper.win_rate, 0)}%`} />
                                <Tile label="DD / max" value={`${fmt(paper.dd_pct_of_budget, 1)}% / ${fmt(paper.max_dd_pct_of_budget, 1)}%`}
                                    tone={paper.max_dd_pct_of_budget > 100 * (paper.params?.dd_stop_pct || 0.08) * 0.8 ? C.warn : C.text}
                                    hint={`hard stop at ${fmt((paper.params?.dd_stop_pct || 0) * 100, 0)}% of budget`} />
                                <Tile label="Markout 30s" value={`${fmt(paper.markout_bps?.["30s"]?.mean, 1)} bps (${paper.markout_bps?.["30s"]?.n || 0})`}
                                    tone={(paper.markout_bps?.["30s"]?.mean || 0) >= 0 ? C.bull : C.bear} />
                                <Tile label="Spacing $" value={paper.spacing_usd ? `$${fmt(paper.spacing_usd, 0)}` : "—"}
                                    hint={`k=${paper.params?.k_sigma} × hourly σ`} />
                                <Tile label="Paused time" value={`${fmt(paper.time_paused_pct, 0)}%`} />
                            </div>

                            <div className="grid md:grid-cols-2 gap-3 mt-3">
                                <div className="border overflow-x-auto" style={{ borderColor: C.border }}>
                                    <div className="text-[10px] uppercase tracking-[0.12em] px-2 pt-2" style={{ color: C.dim }}>
                                        Ladder — k={paper.params?.k_sigma} · {paper.rungs?.length || 0}/{paper.params?.depth} rungs
                                    </div>
                                    <table className="w-full" data-testid="bp-paper-ladder">
                                        <thead>
                                            <tr className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>
                                                <th className="px-2 py-1.5 text-left">Rung</th>
                                                <th className="px-2 py-1.5 text-right">State</th>
                                                <th className="px-2 py-1.5 text-right">Sell at</th>
                                                <th className="px-2 py-1.5 text-right">$</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(paper.rungs || []).length === 0 && (
                                                <tr><td colSpan={4} className="px-2 py-2 text-[11px]" style={{ color: C.dim }}>
                                                    {paper.active ? "ladder rebuilds on the next mark" : "no open rungs"}
                                                </td></tr>
                                            )}
                                            {(paper.rungs || []).map((r) => (
                                                <tr key={r.price}>
                                                    <td className="px-2 py-1 font-mono text-[11px]" style={{ color: r.held ? C.bull : C.sub }}>
                                                        ${Number(r.price).toLocaleString()}
                                                    </td>
                                                    <td className="px-2 py-1 text-[10px] text-right uppercase" style={{ color: r.held ? C.bull : C.dim }}>
                                                        {r.held ? "held" : "resting buy"}
                                                    </td>
                                                    <td className="px-2 py-1 font-mono text-[11px] text-right" style={{ color: C.sub }}>
                                                        {r.sell_at ? `$${Number(r.sell_at).toLocaleString()}` : "—"}
                                                    </td>
                                                    <td className="px-2 py-1 font-mono text-[11px] text-right" style={{ color: C.dim }}>
                                                        {fmt(r.qty * r.price, 0)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="border overflow-x-auto" style={{ borderColor: C.border }}>
                                    <div className="text-[10px] uppercase tracking-[0.12em] px-2 pt-2" style={{ color: C.dim }}>
                                        Recent simulated fills
                                    </div>
                                    <table className="w-full" data-testid="bp-paper-fills">
                                        <thead>
                                            <tr className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>
                                                <th className="px-2 py-1.5 text-left">When</th>
                                                <th className="px-2 py-1.5 text-right">Side</th>
                                                <th className="px-2 py-1.5 text-right">Price</th>
                                                <th className="px-2 py-1.5 text-right">P&L</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(paper.fills || []).length === 0 && (
                                                <tr><td colSpan={4} className="px-2 py-2 text-[11px]" style={{ color: C.dim }}>no fills yet</td></tr>
                                            )}
                                            {(paper.fills || []).slice(0, 10).map((fl, i) => (
                                                <tr key={`${fl.ts_ms}-${i}`}>
                                                    <td className="px-2 py-1 font-mono text-[11px]" style={{ color: C.dim }}>
                                                        {new Date(fl.ts_ms).toLocaleTimeString()}
                                                    </td>
                                                    <td className="px-2 py-1 text-[10px] text-right uppercase"
                                                        style={{ color: fl.side === "BUY" ? C.accent : fl.side === "SELL" ? C.bull : C.bear }}>
                                                        {fl.side}
                                                    </td>
                                                    <td className="px-2 py-1 font-mono text-[11px] text-right" style={{ color: C.sub }}>
                                                        ${Number(fl.price).toLocaleString()}
                                                    </td>
                                                    <td className="px-2 py-1 font-mono text-[11px] text-right"
                                                        style={{ color: fl.pnl === null || fl.pnl === undefined ? C.dim : fl.pnl >= 0 ? C.bull : C.bear }}>
                                                        {fl.pnl === null || fl.pnl === undefined ? "—" : `$${fmt(fl.pnl)}`}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {paper.equity_curve?.length > 1 && (
                                <div className="mt-3">
                                    <div className="text-[10px] uppercase tracking-[0.12em] mb-1" style={{ color: C.dim }}>
                                        Paper equity (dashed = budget ${fmt(paper.budget_usdt, 0)})
                                    </div>
                                    <EquityCurve curve={paper.equity_curve} budget={paper.budget_usdt} testId="bp-paper-curve" />
                                </div>
                            )}

                            <div className="text-[10px] mt-2" style={{ color: C.dim }} data-testid="bp-paper-tune-line">
                                auto-tune {paper.auto_tune ? `ON — replays the last 48h every ${fmt(paper.auto_tune_hours, 0)}h and applies the best k` : "off"}
                                {paper.last_tune && (
                                    <> · last: {fmtTs(paper.last_tune.ts_ms)} — {paper.last_tune.reason}
                                        {paper.last_tune.best_k !== undefined ? ` (best k=${paper.last_tune.best_k}${paper.last_tune.applied ? ", applied" : ""})` : ""}</>
                                )}
                                {paper.auto_tune && paper.next_tune_ms ? ` · next due ${fmtTs(paper.next_tune_ms)}` : ""}
                            </div>
                        </>
                    )}
                </div>

                {runs.length > 0 && (
                    <>
                        <div className="text-[11px] uppercase tracking-[0.16em] mb-2" style={{ color: C.sub }}>
                            Saved runs (click to reload · newest first)
                        </div>
                        <div className="border mb-4 overflow-x-auto" style={{ borderColor: C.border, background: C.panel }}>
                            <table className="w-full" data-testid="bp-runs-table">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>
                                        <th className="px-2 py-2 text-left">When</th>
                                        <th className="px-2 py-2 text-left">Note</th>
                                        <th className="px-2 py-2 text-right">Window</th>
                                        <th className="px-2 py-2 text-right">Best k</th>
                                        <th className="px-2 py-2 text-right">Best P&L</th>
                                        <th className="px-2 py-2 text-right">Markout 30s @best</th>
                                        <th className="px-2 py-2 text-right">ks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {runs.map((r) => {
                                        const best = (r.results || []).find((x) => x.params?.k_sigma === r.best_k) || {};
                                        const mk = best.markout_bps?.["30s"]?.mean;
                                        return (
                                            <tr key={r.run_id} className="cursor-pointer hover:bg-[#121C28]"
                                                onClick={() => fetchSgBlueprintRun(r.run_id)
                                                    .then((full) => { setReplay(full); setSel(0); toast.success(`loaded ${r.run_id}`); })
                                                    .catch((e) => toast.error(`failed to load run: ${apiErr(e)}`))}
                                                data-testid={`bp-run-${r.run_id}`}>
                                                <td className="px-2 py-1.5 font-mono text-[11px]" style={{ color: C.sub }}>{fmtTs(r.ts_ms)}</td>
                                                <td className="px-2 py-1.5 text-[11px]" style={{ color: C.text }}>{r.note || "—"}</td>
                                                <td className="px-2 py-1.5 font-mono text-[11px] text-right" style={{ color: C.dim }}>{r.hours}h · {(r.rows || 0).toLocaleString()}</td>
                                                <td className="px-2 py-1.5 font-mono text-[11px] text-right" style={{ color: C.accent }}>{r.best_k ?? "—"}</td>
                                                <td className="px-2 py-1.5 font-mono text-[11px] text-right"
                                                    style={{ color: (best.net_pnl || 0) >= 0 ? C.bull : C.bear }}>${fmt(best.net_pnl)}</td>
                                                <td className="px-2 py-1.5 font-mono text-[11px] text-right"
                                                    style={{ color: (mk || 0) >= 0 ? C.bull : C.bear }}>{fmt(mk, 1)}</td>
                                                <td className="px-2 py-1.5 font-mono text-[11px] text-right" style={{ color: C.dim }}>{(r.results || []).length}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                <div className="text-[10px] leading-relaxed" style={{ color: C.dim }}>
                    Fill model: a resting buy fills when the recorded AsterDex mid touches it; its sell fills one
                    spacing above; fees per side. Markouts are measured against the <span style={{ color: C.sub }}>Binance</span> mid
                    at +30s/+5m — negative means fills are being adversely selected (the cost the fill model can't see).
                    Safety pauses replay the <span style={{ color: C.sub }}>recorded</span> leader-venue guard fires and liquidation
                    tape. The drawdown stop flattens and halts for the rest of the run — a grid's catastrophic mode is the slow
                    bleed, and only a hard stop caps it. Every replay is saved to <span style={{ color: C.sub }}>sg_blueprint_runs</span> and
                    listed below for comparison. The <span style={{ color: C.sub }}>paper trading grid</span> runs the same policy forward
                    on live marks with simulated fills (pauses driven by the live guard/crash detectors) — the walk-forward leg:
                    replay picks a k on history, paper shows what it earns on ticks that had not happened yet. With auto-tune on,
                    that loop closes autonomously: every few hours it replays the recent tape and re-spaces the paper grid to the
                    winning k. Everything on this page is shadow/paper: it never places orders.
                    Full manual: <span style={{ color: C.sub }}>BLUEPRINT.md</span> in the repo root.
                </div>
            </div>
        </div>
    );
}
